/* eslint-disable import/no-cycle, @typescript-eslint/tslint/config */
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {
  GetCategoryProductsQueryVariables,
  GetFilteredProductsQueryVariables,
  ProductFilters,
  GetRelatedItemsPreOrderQuery,
  GetDataPreOrderQuery,
  GetFilteredProductsPreOrderQuery,
  GetCategoryProductsPreOrderQuery,
} from '../graphql/queries-schema';
import {Topology} from '@wix/wixstores-client-core/dist/es/src/constants';
import {IGetInitialData, IOldGetInitialData, ISorting} from '../types/galleryTypes';
import {query as oldGetDataQueryString} from '../graphql/oldGetData.graphql';
import {query as oldGetDataQueryPreOrderString} from '../graphql/oldGetDataPreOrder.graphql';
import {query as getDataQueryString} from '../graphql/getData.graphql';
import {query as getDataQueryPreOrderString} from '../graphql/getDataPreOrder.graphql';
import {query as getCategoryProductsQueryString} from '../graphql/getCategoryProducts.graphql';
import {query as getCategoryProductsQueryPreOrderString} from '../graphql/getCategoryProductsPreOrder.graphql';
import {query as getFilteredProductsQueryString} from '../graphql/getFilteredProducts.graphql';
import {query as getFilteredProductsQueryQueryPreOrderString} from '../graphql/getFilteredProductsPreOrder.graphql';
import {query as GetRelatedItemsQueryString} from '../graphql/getRelatedItems.graphql';
import {query as GetRelatedItemsQueryPreOrderString} from '../graphql/getRelatedItemsPreOrder.graphql';
import {query as getDataNewQueryString} from '../graphql/getDataNew.graphql';
import _ from 'lodash';
import {ProductSortField, SortDirection} from '@wix/wixstores-graphql-schema';
import {MAX_PRODUCTS, Experiments} from '../constants';

export interface GetProductsResponse {
  list: GetFilteredProductsPreOrderQuery['catalog']['category']['productsWithMetaData']['list'];
  totalCount: number;
}

export interface GetProductsRequest {
  collectionId: string;
  offset: number;
  limit: number;
  withOptions: boolean;
  withPriceRange: boolean;
  filters?: ProductFilters;
  sorting?: ISorting;
  isPreOrderEnabled: boolean;
}

export class DataApi {
  constructor(private readonly siteStore: SiteStore) {
    //
  }

  public oldGetInitialData(
    {externalId, compId, limit, withPriceRange, withOptions}: IOldGetInitialData,
    isPreOrderEnabled: boolean
  ): Promise<{
    data: GetDataPreOrderQuery;
  }> {
    const query = isPreOrderEnabled ? oldGetDataQueryPreOrderString : oldGetDataQueryString;

    const data: any = {
      query,
      source: 'WixStoresWebClient',
      operationName: 'oldGetData',
      variables: {externalId: externalId || '', compId, limit, withPriceRange, withOptions},
    };

    return this.sendRequest(data) as Promise<{
      data: GetDataPreOrderQuery;
    }>;
  }

  public getInitialData({
    externalId,
    compId,
    limit,
    sort,
    filters,
    offset,
    withOptions,
    withPriceRange,
    mainCollectionId,
  }: IGetInitialData): Promise<{
    data: GetDataPreOrderQuery;
  }> {
    const maxLimit = Math.min(limit, MAX_PRODUCTS);
    let data: any;
    if (
      this.siteStore.experiments.enabled(Experiments.SetGalleryCollectionVelo) ||
      this.siteStore.experiments.enabled(Experiments.GallerySetProductsVelo)
    ) {
      data = {
        query: getDataNewQueryString,
        source: 'WixStoresWebClient',
        operationName: 'getDataNew',
        variables: {
          externalId: externalId || '',
          compId: mainCollectionId ? undefined : compId,
          limit: maxLimit,
          sort,
          filters,
          offset,
          withOptions,
          withPriceRange,
          mainCollectionId,
        },
      };
    } else {
      /* istanbul ignore next - tests for pre order will be added at the following commit */
      const query = this.siteStore.experiments.enabled(Experiments.PreOrderClient)
        ? getDataQueryPreOrderString
        : getDataQueryString;
      data = {
        query,
        source: 'WixStoresWebClient',
        operationName: 'getData',
        variables: {
          externalId: externalId || '',
          compId,
          limit: maxLimit,
          sort,
          filters,
          offset,
          withOptions,
          withPriceRange,
        },
      };
    }

    return this.sendRequest(data) as Promise<{
      data: GetDataPreOrderQuery;
    }>;
  }

  public getRelatedItems({
    externalId = '',
    productIds,
    withOptions,
    withPriceRange,
    isPreOrderEnabled,
  }: {
    externalId: string;
    productIds: string[];
    withOptions: boolean;
    withPriceRange: boolean;
    isPreOrderEnabled: boolean;
  }): Promise<{data: GetRelatedItemsPreOrderQuery}> {
    const query = isPreOrderEnabled ? GetRelatedItemsQueryPreOrderString : GetRelatedItemsQueryString;
    const data: any = {
      query,
      source: 'WixStoresWebClient',
      operationName: 'getRelatedItems',
      variables: {externalId, productIds, withOptions, withPriceRange},
    };

    return this.sendRequest(data) as Promise<{data: GetRelatedItemsPreOrderQuery}>;
  }

  private sendRequest(data: any): Promise<any> {
    return this.siteStore.tryGetGqlAndFallbackToPost(
      this.siteStore.resolveAbsoluteUrl(`/${Topology.STOREFRONT_GRAPHQL_URL}`),
      data
    );
  }

  public async getCategoryProducts(
    variables: GetCategoryProductsQueryVariables,
    isPreOrderEnabled: boolean
  ): Promise<{data: GetCategoryProductsPreOrderQuery}> {
    const query = isPreOrderEnabled ? getCategoryProductsQueryPreOrderString : getCategoryProductsQueryString;

    const data: any = {
      query,
      source: 'WixStoresWebClient',
      operationName: 'getCategoryProducts',
      variables,
    };

    return this.sendRequest(data);
  }

  public async getProductsByOffset(request: GetProductsRequest) {
    const {collectionId, offset, limit, filters, withOptions, withPriceRange, sorting, isPreOrderEnabled} = request;
    const response = await this.getProductsByGraphQL(
      collectionId,
      offset,
      limit,
      filters,
      withOptions,
      withPriceRange,
      isPreOrderEnabled,
      sorting
    );
    const {list, totalCount} = response.data.catalog.category.productsWithMetaData;
    return {list: _.compact(list), totalCount};
  }

  public async getProducts({
    collectionId,
    filters,
    fromIndex,
    sorting,
    toIndex,
    withOptions,
    withPriceRange,
    isPreOrderEnabled,
  }: {
    collectionId: string;
    fromIndex: number;
    toIndex: number;
    withOptions: boolean;
    withPriceRange: boolean;
    isPreOrderEnabled: boolean;
    filters?: ProductFilters;
    sorting?: ISorting;
  }): Promise<GetProductsResponse> {
    const offset = fromIndex;
    const limit = toIndex - fromIndex;
    return this.getProductsByOffset({
      collectionId,
      filters,
      sorting,
      withOptions,
      withPriceRange,
      offset,
      limit,
      isPreOrderEnabled,
    });
  }

  public async getProductsByGraphQL(
    mainCollectionId: string,
    offset: number,
    limit: number,
    filters: ProductFilters | null,
    withOptions: boolean,
    withPriceRange: boolean,
    isPreOrder: boolean,
    sorting?: ISorting
  ): Promise<{data: GetFilteredProductsPreOrderQuery}> {
    const sort = sorting
      ? {
          direction: sorting.direction === 'ASC' ? SortDirection.Ascending : SortDirection.Descending,
          sortField: sorting.field as ProductSortField,
        }
      : null;

    const variables: GetFilteredProductsQueryVariables = {
      mainCollectionId,
      offset,
      limit,
      sort,
      filters,
      withOptions,
      withPriceRange,
    };

    const query = isPreOrder ? getFilteredProductsQueryQueryPreOrderString : getFilteredProductsQueryString;

    const data = {
      variables,
      query,
      source: 'WixStoresWebClient',
      operationName: 'getFilteredProducts',
    };

    return this.sendRequest(data);
  }
}
